import React, { useEffect, useState } from 'react';
import { X, Save, Download, FileText, Trash2 } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { ScrollArea } from '../ui/scroll-area';
import { storage, addXP, XP_PER_NOTE } from '../../lib/storage';
import { toast } from 'sonner';

export const DiaryDrawer = ({ open, onOpenChange }) => {
  const [notes, setNotes] = useState([]);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');

  useEffect(() => {
    if (open) setNotes(storage.get(storage.KEYS.NOTES, []));
  }, [open]);

  const persist = (list) => {
    setNotes(list);
    storage.set(storage.KEYS.NOTES, list);
  };

  const saveNote = () => {
    if (!body.trim()) {
      toast.error('Note empty hai');
      return;
    }
    const n = {
      id: `n_${Date.now()}`,
      title: title.trim() || `Note ${notes.length + 1}`,
      body: body.trim(),
      ts: Date.now(),
    };
    const list = [n, ...notes];
    persist(list);
    addXP(XP_PER_NOTE);
    window.dispatchEvent(new Event('brahmastra:xp'));
    setTitle('');
    setBody('');
    toast.success('Note saved locally', { description: `+${XP_PER_NOTE} XP earned` });
  };

  const deleteNote = (id) => {
    persist(notes.filter(n => n.id !== id));
    toast.success('Note deleted');
  };

  const exportDoc = (note) => {
    const html = `
      <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
      <head><meta charset='utf-8'><title>${note.title}</title></head>
      <body style="font-family: Calibri, Arial, sans-serif; line-height:1.6">
        <h1>${escapeHtml(note.title)}</h1>
        <p style="color:#666;font-size:12px">Brahmastra AI · ${new Date(note.ts).toLocaleString()}</p>
        <div>${escapeHtml(note.body).replace(/\n/g, '<br/>')}</div>
      </body></html>`;
    const blob = new Blob(['\ufeff', html], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${sanitize(note.title)}.doc`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Exported as .doc');
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-[400px] sm:max-w-[400px] p-0 bg-card border-l border-border"
        data-testid="diary-drawer"
      >
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-border">
          <SheetTitle className="flex items-center gap-2 text-[16px]">
            <FileText className="w-4 h-4 text-bblue" />
            My Diary
            <span className="ml-auto text-[10.5px] font-normal text-muted-foreground">
              {notes.length} saved · local
            </span>
          </SheetTitle>
        </SheetHeader>

        <div className="p-5 space-y-3 border-b border-border">
          <Input
            placeholder="Note title (optional)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            data-testid="diary-title-input"
            className="bg-secondary/60 border-border"
          />
          <Textarea
            placeholder="Yahan apne notes likhein ya paste karein..."
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={6}
            data-testid="diary-body-input"
            className="bg-secondary/60 border-border resize-none"
          />
          <div className="flex gap-2">
            <Button
              onClick={saveNote}
              data-testid="diary-save-btn"
              className="flex-1 bg-mint text-[#08120e] hover:brightness-110"
            >
              <Save className="w-4 h-4 mr-1.5" /> Save Note
            </Button>
            <Button
              variant="outline"
              onClick={() => body && exportDoc({ title: title || 'Untitled', body, ts: Date.now() })}
              data-testid="diary-export-current-btn"
              disabled={!body.trim()}
            >
              <Download className="w-4 h-4 mr-1.5" /> Export
            </Button>
          </div>
        </div>

        <ScrollArea className="flex-1 h-[calc(100vh-340px)]">
          <div className="p-5 space-y-2">
            {notes.length === 0 ? (
              <div className="text-center text-muted-foreground text-[13px] py-10" data-testid="diary-empty">
                Abhi koi note nahi hai.<br />
                <span className="text-[11px]">Pehla note save karein aur +{XP_PER_NOTE} XP paayein!</span>
              </div>
            ) : (
              notes.map(n => (
                <div key={n.id} className="p-3 rounded-lg bg-secondary/40 border border-border" data-testid={`note-${n.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-[13px] font-medium truncate">{n.title}</div>
                      <div className="text-[10.5px] text-muted-foreground">{new Date(n.ts).toLocaleString()}</div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => exportDoc(n)}
                        className="p-1 rounded text-muted-foreground hover:text-bblue hover:bg-bblue/10"
                        aria-label="Export"
                        data-testid={`note-export-${n.id}`}
                      >
                        <Download className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => deleteNote(n.id)}
                        className="p-1 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                        aria-label="Delete"
                        data-testid={`note-delete-${n.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  <p className="mt-1.5 text-[12px] text-foreground/80 line-clamp-3 whitespace-pre-wrap">{n.body}</p>
                </div>
              ))
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));
}
function sanitize(s) {
  return String(s).replace(/[^\w\u0900-\u097F-]+/g, '_').slice(0, 48) || 'note';
}

export default DiaryDrawer;
